/* ── WORK_TRACK — Timer ───────────────────────────────────── */

import {
  $,
  esc,
  icon,
  api,
  state,
  toast,
  registerView,
  timerState,
  projectOptions,
  fmtMin,
  fmtHM,
  todayStr,
  loadingHtml,
  emptyHtml
} from './core';
import type { ActiveTimer } from './core';
import type { TimeEntry } from './types';

function savedTimer(): ActiveTimer | null {
  try {
    return JSON.parse(localStorage.getItem('wt_timer') || 'null');
  } catch {
    return null;
  }
}

export function loadTimerView(): void {
  const projSelect = $<HTMLSelectElement>('#timerProject');
  projSelect.innerHTML = '<option value="">Select project…</option>' + projectOptions();
  updateTimerTasks();

  projSelect.addEventListener('change', () => {
    const proj = Number(projSelect.value);
    $<HTMLSelectElement>('#timerTask').innerHTML =
      '<option value="">(no task)</option>' +
      state.tasks
        .filter(t => !proj || t.project_id === proj)
        .map(t => `<option value="${t.id}">${esc(t.title)}</option>`)
        .join('');
  });

  loadTodayLog();
}

function updateTimerTasks(): void {
  const proj = Number($<HTMLSelectElement>('#timerProject').value);
  $<HTMLSelectElement>('#timerTask').innerHTML =
    '<option value="">(no task)</option>' +
    state.tasks
      .filter(t => !proj || t.project_id === proj)
      .map(t => `<option value="${t.id}">${esc(t.title)}</option>`)
      .join('');
}

export function startActiveTimer(): void {
  timerState.active = savedTimer();
  if (!timerState.active) {
    $('#timerDisplay').textContent = '00:00:00';
    return;
  }
  $('.timer-card').classList.add('running');
  $('#timerStart').classList.add('hidden');
  $('#timerStop').classList.remove('hidden');
  $('#timerDiscard').classList.remove('hidden');
  $<HTMLSelectElement>('#timerProject').value = timerState.active.project_id
    ? String(timerState.active.project_id)
    : '';
  $<HTMLInputElement>('#timerNote').value = timerState.active.note || '';
  updateTimerTasks();
  $<HTMLSelectElement>('#timerProject').disabled = true;
  $<HTMLSelectElement>('#timerTask').value = timerState.active.task_id ? String(timerState.active.task_id) : '';
  $<HTMLSelectElement>('#timerTask').disabled = true;
  $<HTMLInputElement>('#timerNote').disabled = true;
  renderTimerElapsed();
  clearInterval(timerState.tick ?? undefined);
  timerState.tick = window.setInterval(renderTimerElapsed, 1000);
}

function renderTimerElapsed(): void {
  if (!timerState.active) return;
  const elapsed = Date.now() - timerState.active.started_at;
  $('#timerDisplay').textContent = fmtHM(elapsed);
}

$('#timerStart').addEventListener('click', () => {
  const projectId = $<HTMLSelectElement>('#timerProject').value;
  if (!projectId) {
    toast('Select a project', true);
    return;
  }
  timerState.active = {
    started_at: Date.now(),
    project_id: Number(projectId),
    task_id: $<HTMLSelectElement>('#timerTask').value ? Number($<HTMLSelectElement>('#timerTask').value) : null,
    note: $<HTMLInputElement>('#timerNote').value.trim()
  };
  localStorage.setItem('wt_timer', JSON.stringify(timerState.active));
  startActiveTimer();
  toast('Timer started');
});

$('#timerStop').addEventListener('click', async () => {
  if (!timerState.active) return;
  const elapsedMs = Date.now() - timerState.active.started_at;
  const minutes = Math.max(1, Math.round(elapsedMs / 60000));
  const payload = {
    project_id: timerState.active.project_id,
    task_id: timerState.active.task_id,
    minutes,
    note: timerState.active.note || '',
    entry_date: new Date(timerState.active.started_at).toISOString().slice(0, 10),
    started_at: timerState.active.started_at,
    ended_at: Date.now()
  };
  try {
    $<HTMLButtonElement>('#timerStop').disabled = true;
    await api('/api/entries', 'POST', payload);
    clearActiveTimer();
    loadTodayLog();
    toast(`${fmtMin(minutes)} logged`);
  } catch (e) {
    $<HTMLButtonElement>('#timerStop').disabled = false;
    toast((e as Error).message, true);
  }
});

$('#timerDiscard').addEventListener('click', () => {
  if (!confirm('Discard this running session?')) return;
  clearActiveTimer();
  toast('Timer discarded');
});

function clearActiveTimer(): void {
  clearInterval(timerState.tick ?? undefined);
  timerState.active = null;
  localStorage.removeItem('wt_timer');
  $('#timerDisplay').textContent = '00:00:00';
  $('#timerStart').classList.remove('hidden');
  $('#timerStop').classList.add('hidden');
  $('#timerDiscard').classList.add('hidden');
  $<HTMLSelectElement>('#timerProject').disabled = false;
  $<HTMLSelectElement>('#timerTask').disabled = false;
  $<HTMLInputElement>('#timerNote').disabled = false;
  $<HTMLInputElement>('#timerNote').value = '';
  $<HTMLButtonElement>('#timerStop').disabled = false;
  const card = $('.timer-card');
  if (card) card.classList.remove('running');
}

export async function loadTodayLog(): Promise<void> {
  const ul = $('#timerLog');
  ul.innerHTML = loadingHtml();
  try {
    const entries = await api<TimeEntry[]>(`/api/entries?date=${todayStr()}`);
    if (!entries.length) {
      ul.innerHTML = emptyHtml(
        icon('timer', 30),
        'Nothing logged today',
        'Start the timer above to track today\u2019s work.'
      );
      return;
    }
    ul.innerHTML = entries
      .map(
        e => `
      <li>
        <span class="item-icon">${icon('timer', 18)}</span>
        <div class="item-main">
          <div class="item-title">${esc(e.project_name || 'General')}${e.task_title ? ' · ' + esc(e.task_title) : ''}</div>
          <div class="item-sub">${esc(e.note || '')}</div>
        </div>
        <div class="item-right"><span class="item-val">${fmtMin(e.minutes)}</span></div>
      </li>`
      )
      .join('');
  } catch {
    ul.innerHTML = '<li class="empty">Could not load.</li>';
  }
}

registerView('timer', loadTimerView);
